import type { DropPointInput } from '@chukua/common';
import { Button } from 'antd';
import { MapPin, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { DropPointModal } from './DropPointModal';
import { DropPointRow } from './DropPointRow';
import type { DropPoint } from '../../../jobs.api';

interface DropPointListProps {
  dropPoints: DropPoint[];
  saving: boolean;
  onAdd: (input: DropPointInput) => Promise<boolean>;
  onUpdate: (dropPointId: string, input: DropPointInput) => Promise<boolean>;
  onRemove: (dropPointId: string) => Promise<boolean>;
  onReorder: (dropPoints: DropPoint[]) => Promise<boolean>;
}

function move(dropPoints: DropPoint[], index: number, delta: number): DropPoint[] {
  const target = index + delta;
  if (target < 0 || target >= dropPoints.length) return dropPoints;
  const next = [...dropPoints];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * The ordered stops on a loose-cargo job's route (PRD → Job intake → Captured per job type (Loose
 * cargo); gitaumoses4/chukua#32) — add/edit/remove, plus move-up/move-down to reorder. No drag
 * library exists in this repo, and the acceptance criteria only asks stops to be reorderable and
 * to persist, not a specific interaction.
 */
export function DropPointList({ dropPoints, saving, onAdd, onUpdate, onRemove, onReorder }: DropPointListProps) {
  const [editing, setEditing] = useState<DropPoint | 'new' | null>(null);

  async function submit(input: DropPointInput) {
    if (editing === 'new') await onAdd(input);
    else if (editing) await onUpdate(editing.id, input);
    setEditing(null);
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-fg">Drop points</h2>
        <Button size="small" icon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
          Add drop point
        </Button>
      </div>
      {dropPoints.length === 0 && <EmptyState icon={MapPin} title="No drop points yet" description="Add the ordered stops on this job's route." />}
      {dropPoints.length > 0 && (
        <ol className="flex flex-col gap-3">
          {dropPoints.map((d, index) => (
            <DropPointRow
              key={d.id}
              dropPoint={d}
              position={index + 1}
              isFirst={index === 0}
              isLast={index === dropPoints.length - 1}
              saving={saving}
              onEdit={() => setEditing(d)}
              onRemove={() => onRemove(d.id)}
              onMoveUp={() => onReorder(move(dropPoints, index, -1))}
              onMoveDown={() => onReorder(move(dropPoints, index, 1))}
            />
          ))}
        </ol>
      )}
      <DropPointModal
        open={editing !== null}
        dropPoint={editing === 'new' ? null : editing}
        submitting={saving}
        onSubmit={async (input) => {
          await submit(input);
          return true;
        }}
        onCancel={() => setEditing(null)}
      />
    </section>
  );
}
